import * as Sentry from '@sentry/react';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr';
import { InvokeRpc, ServerRpc } from '../../util/hubs/types';
import { createStore, StoreApi } from 'zustand';

interface RpcClient<T> {
  [key: string]: (x: T, ...args: any[]) => T | null;
}

type GlobalRef<T> = {
  getInstance: () => T;
  setInstance: (x: T | null) => void;
};

export const globalRef: () => GlobalRef<any> = () => {
  let hub: any = null;

  return {
    getInstance() {
      return hub;
    },
    setInstance(next: any) {
      hub = next;
    }
  };
};
type BaseStore<J, T extends ServerRpc<T>> = {
  data: J | null;
  connect: (onConnected?: (cb: InvokeRpc<T>) => void) => void;
  invoke: InvokeRpc<T>;
  initalJoin: boolean;
  hub: HubConnection | null;
};

type StoreWithMutations<TData, TInvoke extends ServerRpc<TInvoke>, TMutations = {}> = {
  data: TData | null;
  connect: (onConnected?: (cb: InvokeRpc<TInvoke>) => void) => void;
  invoke: InvokeRpc<TInvoke>;
  initalJoin: boolean;
  hub: HubConnection | null;
} & TMutations;
export const createHubStore = <
  TInvokeType extends ServerRpc<TInvokeType>,
  TDataType,
  TExtra extends Record<string, (...args: any[]) => any> = {}
>(
  hubRef: GlobalRef<HubConnection>,
  url: string,
  initalData: TDataType,
  clientRpc: RpcClient<TDataType>,
  spreadFn: (x: TDataType) => TDataType,
  initalJoin?: string,
  extraMutations?: (
    set: (
      partial:
        | Partial<BaseStore<TDataType, TInvokeType>>
        | ((state: BaseStore<TDataType, TInvokeType>) => Partial<BaseStore<TDataType, TInvokeType>>)
    ) => void,
    get: () => BaseStore<TDataType, TInvokeType>
  ) => TExtra
) => {
  return createStore<StoreWithMutations<TDataType, TInvokeType>>()((set, get) => {
    let mutations = {};
    if (extraMutations) {
      mutations = extraMutations(
        (partial: any) => set(partial),
        () => get() as BaseStore<TDataType, TInvokeType>
      );
    }

    return {
      ...mutations,
      hub: null,
      data: initalData,
      initalJoin: false,
      connect(onConnected) {
        if (hubRef.getInstance() == null) {
          let message: any | null = get().data;
          const initConnection = new HubConnectionBuilder()
            .withUrl(url)
            .withAutomaticReconnect([0, 2000, 10000, 30000, 60000, 120000])
            .build();

          const functions = Object.keys(clientRpc);
          for (const procedure of functions) {
            initConnection.on(procedure, (...args) => {
              if (message != null) {
                set((state) => {
                  message = clientRpc[procedure](state.data as any, ...args);
                  if (message == null) {
                    return {
                      ...state
                    };
                  }
                  return {
                    ...state,
                    data: spreadFn(message)
                  };
                });
              }
            });
          }
          if (initalJoin) {
            initConnection.on(initalJoin, () => {
              set((state) => ({ ...state, initalJoin: true }));
            });
          }
          initConnection
            .start()
            .then(() => {
              const hub = initConnection;
              onConnected?.((m, ...args) => {
                return hub.invoke(m.toString(), ...args);
              });
            })
            .catch((e) => {
              Sentry.captureException(e);
            });
          hubRef.setInstance(initConnection);
        } else {
          onConnected?.((m, ...args) => {
            return hubRef.getInstance().invoke(m.toString(), ...args);
          });
        }
      },
      invoke(methodName, ...args) {
        return (
          hubRef.getInstance().invoke(methodName.toString(), ...args) ??
          new Promise((r) => {
            r(undefined);
          })
        );
      }
    };
  }) as StoreApi<
    {
      data: TDataType | null;
      connect: (onConnected?: ((cb: InvokeRpc<TInvokeType>) => void) | undefined) => void;
      invoke: InvokeRpc<TInvokeType>;
      initalJoin: boolean;
      hub: HubConnection | null;
    } & TExtra
  >;
};
